import { Subscribable, Subscription } from '@benzinga/subscribable';
import { safeMap, SafeError, SafePromise, SafeType } from '@benzinga/safe-await';
import { Expression, Session } from '@benzinga/session';
import { oneRequestAtATime } from '@benzinga/utils';
import { NewsRequest, NewsRequestEvent } from './request';
import { NewsSocket, NewsSocketEvent } from './socket';
import { NewsStore, NewsStoreEvent } from './store';
import {
  NodeId,
  NewsfeedFieldType,
  Source,
  Category,
  NewsBZQLQuery,
  SocketStory,
  RestfulStory,
  SourceGroup,
} from './entities';
import { Story } from './story';
import { NewsFeed } from './feed';
import { ContentTypes, PressRelease } from './ingress';
import { DateTime } from 'luxon';

interface StoryEvent {
  story: Story;
  type: 'news:story';
}

interface StoriesEvent {
  Stories: Story[];
  type: 'news:stories';
}

interface ErrorEvent {
  error?: SafeError;
  errorType: 'news:storyId_query_returned_more_then_one_story' | `news:nodeId_query_returned_more_then_one_story`;
  type: 'error';
}

export type AdvancedNewsManagerEvent =
  | StoryEvent
  | StoriesEvent
  | NewsStoreEvent
  | NewsRequestEvent
  | NewsSocketEvent
  | ErrorEvent;

export class AdvancedNewsManager extends Subscribable<AdvancedNewsManagerEvent> {
  private store: NewsStore;
  private request: NewsRequest;
  private socket: NewsSocket;
  private requestSubscription?: Subscription<NewsRequest>;
  private storeSubscription?: Subscription<NewsStore>;
  private socketSubscription?: Subscription<NewsSocket>;
  private getContentTypesLimited = oneRequestAtATime(() => this.getContentTypes());

  constructor(session: Session) {
    super();
    this.store = new NewsStore(this);
    this.socket = new NewsSocket(session);
    this.request = new NewsRequest(session);
  }

  public static getName = () => 'benzinga-advanced-news';

  public getCategories = async (): SafePromise<Category[]> =>
    safeMap(this.getContentTypesLimited(), contentType => contentType.categories);

  public getSources = async (): SafePromise<Source[]> =>
    safeMap(this.getContentTypesLimited(), contentType => contentType.sources);

  public getSourcesGroup = async (): SafePromise<SourceGroup[]> =>
    safeMap(this.getContentTypesLimited(), contentType => contentType.sourcesGroups);

  public getPressReleases = async (): SafePromise<PressRelease[]> =>
    safeMap(this.getContentTypesLimited(), contentType => contentType.pressRelease);

  public getStory = async (id: NodeId, fields?: NewsfeedFieldType[]): SafePromise<Story> => {
    const cachedStory = this.store.getStory(id);
    if (cachedStory) {
      if (fields === undefined || cachedStory.hasFields(fields)) {
        return { ok: cachedStory };
      }
    }

    if (this.socket.getState() === 'running') {
      const stories = await this.getStories({
        fields,
        where: Expression.fromToken<NewsfeedFieldType>(['NodeID', 'eq', id]),
      });
      if (stories.err) {
        return { err: stories.err };
      } else {
        return this.checkOnlyOneStoryInArray(stories.ok);
      }
    } else {
      const story = await this.request.getStory(id);
      if (story.err) {
        return { err: story.err };
      } else {
        return { ok: await this.store.processStory(story.ok) };
      }
    }
  };

  public getStories = async (query: Partial<NewsBZQLQuery>, forceSocket?: boolean): SafePromise<Story[]> => {
    const stories = await this.getStoriesRaw(query, forceSocket);
    if (stories.err) {
      return { err: stories.err };
    } else {
      return { ok: await this.store.processMultipleHistoricStories(stories.ok) };
    }
  };

  public getStoriesRaw = async (
    query: Partial<NewsBZQLQuery>,
    forceSocket?: boolean,
  ): SafePromise<(SocketStory | RestfulStory)[]> => {
    if (this.socket.getState() === 'running' || forceSocket) {
      return this.socket.query(query);
    } else {
      return this.request.getStories(query);
    }
  };

  public getStoryBody = async (story: Story): SafePromise<string> => {
    const storyWithBody = await this.getStory(story.getNodeId(), ['Body']);
    if (storyWithBody.err) {
      return { err: storyWithBody.err };
    } else {
      return { ok: storyWithBody.ok.getBody() ?? '' };
    }
  };

  public createFeed = (query: Partial<NewsBZQLQuery>, jumpedToDate?: DateTime): NewsFeed => {
    return new NewsFeed(this.socket.createFeed(query, jumpedToDate), this.store);
  };

  public getActiveFeeds = (): NewsFeed[] => this.store.getActiveNewsFeeds();
  public getStoredSources = (): Source[] | undefined => this.store.getContentTypes()?.sources;
  public getStoredSourcesGroups = (): SourceGroup[] | undefined => this.store.getContentTypes()?.sourcesGroups;
  public getStoredPressReleases = (): PressRelease[] | undefined => this.store.getContentTypes()?.pressRelease;
  public getStoredCategories = (): Category[] | undefined => this.store.getContentTypes()?.categories;
  public getStoredCategoriesList = (): Category[] | undefined => this.store.getCategoriesList();

  public getContentTypes = async (): SafePromise<ContentTypes> => {
    const cachedContextTypes = this.store.getContentTypes();
    if (cachedContextTypes) {
      return { ok: cachedContextTypes };
    }
    const contentTypes = await this.request.getContentTypes();
    if (contentTypes.err) {
      return contentTypes as SafeType<ContentTypes>;
    } else {
      this.store.setContentTypes(contentTypes.ok);
      return { ok: contentTypes.ok };
    }
  };

  public start = (session: Session): void => {
    //early exit for a open socket
    if (this.socket.getState() === 'running') {
      return;
    }
    this.socket = new NewsSocket(session);
  };

  // Completely disconnect socket
  public stop = (): void => {
    this.socket.dispatch({ type: 'news:disconnected' });
    this.socket['socketSubscription']?.unsubscribe();
    this.socket['state'] = 'stopped';
  };

  protected onFirstSubscription(): void {
    this.storeSubscription = this.store.listen(event => this.dispatch(event));
    this.socketSubscription = this.socket.listen(event => this.dispatch(event));
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
  }

  protected onZeroSubscriptions(): void {
    this.requestSubscription?.unsubscribe();
    this.storeSubscription?.unsubscribe();
    this.socketSubscription?.unsubscribe();
  }

  private checkOnlyOneStoryInArray = async (stories: Story[]): SafePromise<Story> => {
    if (stories.length === 1) {
      const story = stories[0];
      return { ok: await story };
    } else {
      const error = new SafeError('news:nodeId query returned more then one story', 'newsfeed', stories);
      this.dispatch({
        error,
        errorType: 'news:nodeId_query_returned_more_then_one_story',
        type: 'error',
      });
      return { err: error };
    }
  };
}
